import React, { useState, useEffect } from "react";

export default function HsnById({
  initialValues,
  onSubmit,
  onCancel,
  viewOnly,
  isEdit
}) {

  /* -------------------- FIELD CONFIG -------------------- */

  const hsnFields = [
    { name: "hsnCode", label: "HSN Code", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", required: true },
    { name: "taxExempted", label: "Tax Exempted", type: "checkbox" },
    { name: "cgstRate", label: "CGST (%)", type: "number", required: true },
    { name: "sgstRate", label: "SGST (%)", type: "number", required: true },
    { name: "igstRate", label: "IGST (%)", type: "number", required: true },
    { name: "cessRate", label: "CESS (%)", type: "number" },
    { name: "effectiveTax", label: "Effective Tax (%)", type: "number", readonly: true },
    { name: "effectiveFromDate", label: "Effective From", type: "date", required: true },
    { name: "effectiveToDate", label: "Effective To", type: "date" },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" }
      ]
    }
  ];

  /* -------------------- STATE -------------------- */

  const [formData, setFormData] = useState({
    hsnCode: "",
    description: "",
    taxExempted: false,
    cgstRate: 0,
    sgstRate: 0,
    igstRate: 0,
    cessRate: 0,
    effectiveTax: 0,
    effectiveFromDate: "",
    effectiveToDate: "",
    status: "ACTIVE",
    countryCode: "IN",
    taxType: "1"
  });

  /* -------------------- AUTO TAX CALCULATION -------------------- */

  useEffect(() => {
    const total =
      Number(formData.cgstRate || 0) +
      Number(formData.sgstRate || 0) +
      Number(formData.igstRate || 0) +
      Number(formData.cessRate || 0);

    setFormData(prev => ({
      ...prev,
      effectiveTax: total
    }));
  }, [
    formData.cgstRate,
    formData.sgstRate,
    formData.igstRate,
    formData.cessRate
  ]);

  /* -------------------- EDIT / VIEW MODE -------------------- */

  useEffect(() => {
    if ((viewOnly || isEdit) && initialValues) {
      setFormData({
        ...initialValues,
        effectiveFromDate: initialValues.effectiveFromDate?.slice(0, 10),
        effectiveToDate: initialValues.effectiveToDate
          ? initialValues.effectiveToDate.slice(0, 10)
          : ""
      });
    }
  }, [initialValues, viewOnly, isEdit]);

  /* -------------------- HANDLERS -------------------- */

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const submit = async (e) => {
    e.preventDefault();

    const finalData = {
      ...formData,
      effectiveFromDate: new Date(formData.effectiveFromDate),
      effectiveToDate: formData.effectiveToDate
        ? new Date(formData.effectiveToDate)
        : null
    };

    if (isEdit) {
      await onSubmit(finalData, initialValues._id);
    } else {
      await onSubmit(finalData);
    }
  };


  /* -------------------- UI -------------------- */

  return (
    <form>
      <div className="d-flex flex-wrap gap-3">
        {hsnFields.map(field => (
          <div
            key={field.name}
            className="mb-2"
            style={{ flex: "0 0 22%" }}
          >
            <label className="form-label">
              {field.label}
              {field.required && (
                <span className="text-danger"> *</span>
              )}
            </label>

            {field.type === "select" ? (
              <select
                className="form-select"
                value={formData[field.name] || ""}
                onChange={e =>
                  handleChange(field.name, e.target.value)
                }
                disabled={viewOnly || field.readonly}
              >
                <option value="">Select</option>
                {field.options.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : field.type === "checkbox" ? (
              <input
                type="checkbox"
                checked={formData[field.name] || false}
                onChange={e =>
                  handleCheckboxChange(
                    field.name,
                    e.target.checked
                  )
                }
                disabled={viewOnly}
              />
            ) : field.type === "textarea" ? (
              <textarea
                className="form-control"
                value={formData[field.name] || ""}
                onChange={e =>
                  handleChange(field.name, e.target.value)
                }
                readOnly={viewOnly || field.readonly}
              />
            ) : (
              <input
                type={field.type}
                className="form-control"
                value={formData[field.name] || ""}
                onChange={e =>
                  handleChange(field.name, e.target.value)
                }
                readOnly={viewOnly || field.readonly}
              />
            )}
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-center gap-2 mt-3">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
        >
          Back
        </button>

        {!viewOnly && (
          <button
            type="submit"
            className="btn btn-primary"
            onClick={submit}
          >
            {isEdit ? "Update" : "Save"}
          </button>
        )}
      </div>
    </form>
  );
}
